import { Button, Input, Select, Spin } from "antd";
import axios from "axios";
import debounce from "lodash/debounce";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { AiOutlineEye } from "react-icons/ai";
import { FaRegUser } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { DataTable } from "@/components";
import { userRole } from "@/constant";
import { getFormatedUserType } from "@/utils/userTypeFomater";
import { flare } from "@flareapp/js";

const AssignCustomReportTemplate = () => {
  const [businessesList, setBusinessesList] = useState([]);
  const { userDetails } = useSelector((state) => state.user);

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [role, setRole] = useState();
  const [reportList, setReportList] = useState([]);

  const [assignList, setAssignList] = useState([]);
  const [assignListTotal, setAssignListTotal] = useState(0);
  const [assignLimit, setAssignLimit] = useState(10);
  const [assignPage, setAssignPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  const [formData, setFormData] = useState({});
  const { selectedBusiness, report } = formData;

  // get location
  const getLocation = async () => {
    const response = await axios.get(`/site-location/my-site`, {
      withCredentials: true,
      params: {
        business: selectedBusiness,
        limit: 100,
      },
    });
    setLocations(response?.data);
  };

  // get site user
  const getUsers = async () => {
    // setIsLoading(true);
    try {
      if (formData.siteLocation) {
        const { results } = await api.get(endpoint.all_users + "/include-me", {
          params: {
            sortBy: "createdAt",
            orderBy: "DESC",
            role,
            site: formData.siteLocation,
            limit: 100000,
            page: 1,
            includeRequestSender: true,
          },
        });
        setUsers(results);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }

    // setIsLoading(false);
  };

  // get report list
  const getReportTemplateList = async () => {
    if (!isLoading) {
      setIsLoading(true);
      const response = await api.get(endpoint.get_customReport_template_list, {
        params: {
          limit: 1000,
          page: 1,
        },
      });
      setReportList(response?.data || []);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${appConfig.name} - Assign Report Template`;
    getReportTemplateList();
  }, []);

  // get All AssignList
  const getAllAssignList = debounce(async () => {
    try {
      setIsLoading(true);
      const { results, count } = await api.get(endpoint.get_customReport_template_assign, {
        params: {
          limit: assignLimit,
          page: assignPage,
          sortBy: "createdAt",
          orderBy: "DESC",
          searchText,
        },
      });
      // console.log({ results, count });
      setAssignList(results);
      setAssignListTotal(count);
      setIsLoading(false);
    } catch (error) {
      flare.report(error);
      Swal.fire({
        title: "Ooops",
        text: error.message,
        icon: "danger",
        // cancelButtonColor: "#d33",
        // showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
    }
  }, 1000);

  const getBusiness = async () => {
    try {
      const data = await api.get(endpoint.business);
      setBusinessesList(data?.data);
      if (Array.isArray(data?.data) && data.data.length === 1) {
        setFormData({ ...formData, selectedBusiness: data.data[0]._id });
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedBusiness) {
      getLocation();
    }
  }, [selectedBusiness]);

  useEffect(() => {
    getBusiness();
  }, [assignLimit, assignPage]);

  useEffect(() => {
    getAllAssignList();
  }, [assignLimit, assignPage, searchText]);

  useEffect(
    () => {
      getUsers({ report });
    },
    [role, formData.siteLocation, report],
    selectedBusiness,
  );

  const handleRemoveAssign = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirm",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await api.delete(`${endpoint.assign_customReport}/${id}`);
        getAllAssignList();
        Swal.fire({
          title: "Deleted!",
          text: "User Unassigned to Report Successfully.",
          icon: "success",
          timer: 1500,
        });
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const body = {
      site: formData.siteLocation,
      user: formData.siteUser,
      customReports: report,
      business: formData.selectedBusiness,
    };

    try {
      const res = await axios.post(endpoint.assign_multi_template, body, {
        withCredentials: true,
      });
      if (res) {
        setFormData({});
        Swal.fire({
          title: "",
          text: "User assigned successfully.",
          icon: "success",
          timer: 2000,
        });
      }
    } catch (error) {
      flare.report(error);
      if (error?.response) {
        Swal.fire({
          title: "",
          text: error?.response?.data?.message || "User is already assigned to this report",
          icon: "danger",
          timer: 2000,
        });
      }
    } finally {
      getAllAssignList();
      getUsers({});
      setIsLoading(false);
    }
  };

  const columnsList = [
    {
      title: "No.",
      render: (row, _, index) => index + 1,
      width: "60px",
    },
    {
      title: "Template Name",
      render: (row) => row.customReports?.name,
    },
    {
      title: "User Name",
      render: (row) => row.user?.name,
    },
    {
      title: "Login ID",
      render: (row) => row.user?.loginId,
      grow: 2,
    },
    // {
    //   title: "Email",
    //   render: (row) => row.user?.email,
    //   grow: 2,
    // },
    {
      title: "User Type",
      render: (row) => getFormatedUserType(row.user?.role),
      align: "center",
    },

    {
      title: "Date Assigned",
      render: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
      align: "center",
    },
    {
      title: "Action",
      width: "220px",
      align: "center",
      render: (row) => (
        <div>
          <Link to={`/users/details/${row.user?._id}`}>
            <button className="me-2 btn btn-view-gray" title="View User">
              <FaRegUser className="text-white" />
            </button>
          </Link>
          <Link to={`/report-template/${row.customReports?._id}`}>
            <button className="me-2 btn btn-az-primary" title="View Template">
              <AiOutlineEye className="text-white" />
            </button>
          </Link>
          <button className="me-2 btn btn-danger" title="Remove Assign" onClick={() => handleRemoveAssign(row._id)}>
            <MdDeleteOutline className="text-white" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="px-md-3 mt-3">
      <div className="border p-3">
        {/* <h5 className="fw-bold">Assign Report</h5> */}
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <label className="az-content-label">Manage Report</label>
            <span className="d-block py-2">Assign Report Template</span>
          </div>
        </div>
        <hr className="mt-0 mb-5" />
        <h6 className="fw-bold mt-4">Assign Existing System User Accounts</h6>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={4}>
              <Form.Group className="my-2">
                <Form.Label>Report Template</Form.Label>
                <Select
                  mode="multiple"
                  placeholder="Select Report Template"
                  value={formData.report}
                  size="large"
                  maxTagCount={1}
                  maxTagTextLength={20}
                  onChange={(value) => {
                    return setFormData({
                      ...formData,
                      report: value,
                    });
                  }}
                  required
                  className="text-secondary w-100"
                  name="report"
                  showSearch={true}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  options={reportList.map((report) => ({
                    value: report._id,
                    label: report.name,
                  }))}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="my-2">
                <Form.Label>Business</Form.Label>
                <Select
                  placeholder="Select Business"
                  value={formData.selectedBusiness}
                  size="large"
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      siteLocation: undefined,
                      selectedBusiness: value,
                    })
                  }
                  required
                  className="text-secondary w-100"
                  name="selectedBusiness"
                  showSearch={true}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  options={businessesList.map((business) => ({
                    value: business._id,
                    label: `${business.name} (${business.id})`,
                  }))}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="my-2">
                <Form.Label>Locations</Form.Label>
                <Select
                  placeholder="Select Site Location"
                  value={formData.siteLocation}
                  size="large"
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      siteLocation: value,
                    })
                  }
                  required
                  className="text-secondary w-100"
                  name="siteLocation"
                  showSearch={true}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  options={[
                    // { value: "", label: "All Site Locations" },
                    ...locations.map((business) => ({ value: business._id, label: business.name })),
                  ]}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="my-2">
                <Form.Label>User Role</Form.Label>
                <Select
                  placeholder="All Users"
                  value={role}
                  size="large"
                  onChange={(value) => setRole(value)}
                  required
                  className="text-secondary w-100"
                  name="role"
                  showSearch={true}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  options={[
                    { label: "All Users", value: "" },
                    ...Object.keys(userRole)
                      .filter((role) => ![userRole.superAdmin, userDetails.role].includes(role))
                      .map((role) => ({ value: role, label: getFormatedUserType(role) })),
                  ]}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="my-2">
                <Form.Label>Site User</Form.Label>
                <Select
                  mode="multiple"
                  placeholder="Select Users"
                  value={formData.siteUser}
                  size="large"
                  maxTagCount={1}
                  maxTagTextLength={20}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      siteUser: value,
                    })
                  }
                  required
                  className="text-secondary w-100"
                  name="siteUser"
                  showSearch={true}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  options={users.map((user) => ({ value: user._id, label: user.name }))}
                />
              </Form.Group>
            </Col>

            <Col xs={12} className="d-flex justify-content-end">
              <div className="marginTop">
                <Button
                  disabled={!formData.siteUser?.length || !formData.siteUser?.length || !formData.report?.length}
                  htmlType="submit"
                  className="px-4 "
                  type="primary"
                >
                  Assign Template
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
        <hr />
        <div className="mt-4">
          {[userRole.superAdmin, userRole.businessAdmin].includes(userDetails.role) && (
            <div>
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="fw-bold"> Assigned List </h6>
                <Input
                  type="search"
                  size="large"
                  value={searchText}
                  onChange={(e) => {
                    setAssignPage(1);
                    setSearchText(e.target.value);
                  }}
                  placeholder="Search"
                  className="me-2"
                  aria-label="Search"
                  style={{ maxWidth: "150px" }}
                />
              </div>
              <Spin spinning={isLoading}>
                <DataTable
                  columns={columnsList}
                  dataSource={assignList}
                  pagination={{
                    pageSize: assignLimit,
                    current: assignPage,
                    total: assignListTotal,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    pageSizeOptions: [10, 20, 50],
                    responsive: true,
                  }}
                  onChange={({ pageSize, current }) => {
                    setAssignLimit(pageSize);
                    setAssignPage(current);
                  }}
                />
              </Spin>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignCustomReportTemplate;
